import { action } from "typesafe-actions";
import { IDataTableV2DateState } from "../../../components/TableV2/preDefinedPlugins/DataTableV2Date/DataTableV2Date.types";
import { ThunkAction } from "redux-thunk";
import { IStoreState } from "../../initialStoreState";
import { AnyAction } from "redux";
import { api } from "../../../api/api";
import {
  saveLoaderCompleted,
  saveLoaderProgress,
  showMessage,
} from "../../messages/messagesActions";
import { IBalanceSheetStatement } from "./BalanceSheetStatements.types";
import { getFileExtensionFromUrl } from "../../../helpers";
import { translateBackendError } from "../../../helpers/errorTranslator";

export const FETCH_BALANCE_SHEET_STATEMENTS_LIST_PROGRESS =
  "FETCH_BALANCE_SHEET_STATEMENTS_LIST_PROGRESS";
export const FETCH_BALANCE_SHEET_STATEMENTS_LIST_SUCCESS =
  "FETCH_BALANCE_SHEET_STATEMENTS_LIST_SUCCESS";
export const FETCH_BALANCE_SHEET_STATEMENTS_LIST_FAILED =
  "FETCH_BALANCE_SHEET_STATEMENTS_LIST_FAILED";

export const fetchBalanceSheetStatementListProgress = () =>
  action(FETCH_BALANCE_SHEET_STATEMENTS_LIST_PROGRESS);
export const fetchBalanceSheetStatementListSuccess = (
  data: IBalanceSheetStatement[],
  totalRecords: number
) =>
  action(FETCH_BALANCE_SHEET_STATEMENTS_LIST_SUCCESS, { data, totalRecords });
export const fetchBalanceSheetStatementListFailed = () =>
  action(FETCH_BALANCE_SHEET_STATEMENTS_LIST_FAILED);

/**
 * Optional branch-filter payload for the Balance Sheet fetch thunks.
 *
 * Exactly one of `branch_codes` / `exclude_branch_codes` should be populated
 * at a time (the include/exclude toggle on the {@link BranchPicker} guarantees
 * this on the UI side). Each is serialized as REPEATED query params, e.g.
 * `&branch_codes=A&branch_codes=B`. An empty/omitted selection sends neither
 * param, which the backend treats as the whole company.
 */
export interface IBalanceSheetBranchFilter {
  branch_codes?: string[];
  exclude_branch_codes?: string[];
}

/**
 * Appends `branch_codes` / `exclude_branch_codes` as REPEATED query params to
 * an already-started query string (which must already contain at least one
 * param so the caller controls the leading `?`). Empty arrays are skipped.
 */
const appendBranchParams = (
  branchFilter: IBalanceSheetBranchFilter | undefined
): string => {
  if (!branchFilter) return "";
  const parts: string[] = [];
  (branchFilter.branch_codes ?? []).forEach((code) => {
    if (code) parts.push(`branch_codes=${encodeURIComponent(code)}`);
  });
  (branchFilter.exclude_branch_codes ?? []).forEach((code) => {
    if (code) parts.push(`exclude_branch_codes=${encodeURIComponent(code)}`);
  });
  return parts.length > 0 ? `&${parts.join("&")}` : "";
};

export const fetchBalanceSheetStatementListAsync =
  (
    status: string,
    date: IDataTableV2DateState["dates"],
    searchState: { [key: string]: any },
    branchFilter?: IBalanceSheetBranchFilter,
    useV3?: boolean
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      const { from_date, to_date } = searchState;
      dispatch(fetchBalanceSheetStatementListProgress());

      // The backend now handles both include and exclude branch modes on V2.
      // Default to V2; the V3 endpoint is kept only for regression comparison
      // and is opt-in via `useV3` (driven by a `?v3=1` URL flag on the view).
      let finalUrl = useV3
        ? `/accounting/get-balance-sheet-data-v3?from_date=${from_date}&to_date=${to_date}`
        : `/accounting/get-balance-sheet-data-v2?from_date=${from_date}&to_date=${to_date}`;

      if (status !== "-1") {
        finalUrl += "&status=" + status;
      }

      finalUrl += appendBranchParams(branchFilter);

      const res = await api.get(finalUrl);
      const data: IBalanceSheetStatement[] = res.data.data;
      const totalRecords = res.data.totalRecords;

      dispatch(fetchBalanceSheetStatementListSuccess(data, totalRecords));
    } catch (err: any) {
      dispatch(fetchBalanceSheetStatementListFailed());
      dispatch(
        showMessage({
          type: "error",
          message: translateBackendError(err.response?.data),
          displayAs: "snackbar",
        })
      );
    }
  };

export const fetchBalanceSheetStatementListForExportAsync =
  (
    date: IDataTableV2DateState["dates"],
    versionV2: boolean,
    onCallback: (isSuccess: boolean) => void,
    branchFilter?: IBalanceSheetBranchFilter,
    useV3?: boolean
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      const { fromDate, toDate } = date;
      dispatch(saveLoaderProgress());

      let finalUrl = useV3
        ? `/accounting/get-balance-sheet-data-v3-pdf?from_date=${fromDate}&to_date=${toDate}`
        : `/accounting/get-balance-sheet-data-v2-pdf?from_date=${fromDate}&to_date=${toDate}`;

      finalUrl += appendBranchParams(branchFilter);

      // if (from_date) {
      //   const filterObj = {
      //     column: ["date"],
      //     operator: "GREATER_THAN_EQUAL",
      //     value: from_date,
      //     logicalOperator: "AND",
      //   };

      //   const encodedFilter = encodeURIComponent(
      //     JSON.stringify(filterObj, null, 2)
      //   );
      //   finalUrl += `&advanceFilter=${encodedFilter}`;
      // }
      // if (to_date) {
      //   const filterObj = {
      //     column: ["date"],
      //     operator: "LESSER_THAN_EQUAL",
      //     value: to_date,
      //     logicalOperator: "AND",
      //   };

      //   const encodedFilter = encodeURIComponent(
      //     JSON.stringify(filterObj, null, 2)
      //   );
      //   finalUrl += `&advanceFilter=${encodedFilter}`;
      // }

      const res = await api.get(finalUrl, {
        responseType: "arraybuffer",
      });

      const blob = new Blob([res.data], {
        type: "application/" + getFileExtensionFromUrl("Balance_sheet.pdf"),
      });
      // const blob = new Blob([res.data], { type: "application/octet-stream" });
      const url = window.URL.createObjectURL(blob);

      // Create a link element and simulate a click to trigger the download
      const link = document.createElement("a");
      link.href = url;
     link.setAttribute("download", "Balance_sheet.pdf");// Set the desired file name and extension
      document.body.appendChild(link);
      link.click();

      onCallback(true);
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: translateBackendError(err.response?.data),
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const CLEAR_BALANCE_SHEET_STATEMENTS = "CLEAR_BALANCE_SHEET_STATEMENTS";
export const clearBalanceSheetStatements = () =>
  action(CLEAR_BALANCE_SHEET_STATEMENTS);
